class TabbedContent extends HTMLElement {
  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    if (!this.tablist) return;

    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    if (!this.tabs.length) return;

    this.tablist.addEventListener('click', this.onClick);
    this.tablist.addEventListener('keydown', this.onKeydown);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    this.updateIndicator();
    this.bindLayoutObservers();
  }

  disconnectedCallback() {
    this.tablist?.removeEventListener('click', this.onClick);
    this.tablist?.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;
    this.selectTab(tab, { focus: false });
  };

  onKeydown = (event) => {
    const currentIndex = this.tabs.indexOf(document.activeElement);
    if (currentIndex === -1) return;

    let nextIndex = -1;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.selectTab(this.tabs[nextIndex], { focus: true });
  };

  onBlockSelect = (event) => {
    const tab = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
    if (tab) this.selectTab(tab, { focus: false });
  };

  selectTab(tab, { focus }) {
    if (!tab || tab.getAttribute('aria-selected') === 'true') {
      if (focus) tab.focus();
      this.updateIndicator();
      return;
    }

    const panelId = tab.getAttribute('aria-controls');

    this.tabs.forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.setAttribute('aria-selected', String(isActive));
      candidate.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.toggleAttribute('hidden', panel.id !== panelId);
    });

    if (focus) tab.focus();
    this.scrollTabIntoView(tab);
    this.updateIndicator();
  }

  scrollTabIntoView(tab) {
    const wrap = this.querySelector('.tabbed-content__tablist-wrap');
    if (!wrap || wrap.scrollWidth <= wrap.clientWidth) return;

    const tabLeft = tab.offsetLeft;
    const tabRight = tabLeft + tab.offsetWidth;
    const viewStart = wrap.scrollLeft;
    const viewEnd = viewStart + wrap.clientWidth;

    if (tabLeft < viewStart) {
      wrap.scrollTo({ left: tabLeft - 12, behavior: 'smooth' });
    } else if (tabRight > viewEnd) {
      wrap.scrollTo({ left: tabRight - wrap.clientWidth + 12, behavior: 'smooth' });
    }
  }

  updateIndicator = () => {
    if (!this.tablist) return;
    const activeTab = this.tablist.querySelector('[aria-selected="true"]');
    if (!activeTab) {
      this.tablist.style.setProperty('--indicator-left', '0px');
      this.tablist.style.setProperty('--indicator-width', '0px');
      return;
    }

    const left = activeTab.offsetLeft;
    const width = activeTab.offsetWidth;
    this.tablist.style.setProperty('--indicator-left', `${left}px`);
    this.tablist.style.setProperty('--indicator-width', `${width}px`);
  };

  bindLayoutObservers() {
    this.onResize = () => this.updateIndicator();
    window.addEventListener('resize', this.onResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateIndicator);
      this.resizeObserver.observe(this.tablist);
      this.tabs.forEach((tab) => this.resizeObserver.observe(tab));
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(this.updateIndicator);
    }
  }
}

if (!customElements.get('tabbed-content')) {
  customElements.define('tabbed-content', TabbedContent);
}
